import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { startGoogleLogin } from "@/pages/Login";
import api from "@/lib/api";
import { Wordmark } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PrivacyPromise } from "@/components/privacy/PrivacyPromise";
import { Loader2, UserPlus, PartyPopper, CheckCircle2 } from "lucide-react";

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
  );
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invite = searchParams.get("invite") || "";
  const ref = searchParams.get("ref") || "";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [pendingMsg, setPendingMsg] = useState("");

  // Persist attribution so it survives the Google OAuth redirect round-trip.
  useEffect(() => {
    if (invite) localStorage.setItem("fc_pending_invite", invite); else localStorage.removeItem("fc_pending_invite");
    if (ref) localStorage.setItem("fc_pending_ref", ref); else localStorage.removeItem("fc_pending_ref");
  }, [invite, ref]);

  // Look up invite details to greet the new member.
  useEffect(() => {
    if (!invite) return;
    api.get(`/invites/public/${invite}`)
      .then((r) => setInviteInfo(r.data))
      .catch(() => setInviteInfo(null));
  }, [invite]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const result = await register(name, email, password, { ref, invite });
      if (result?.pending) {
        setPendingMsg(result.message || "Your request to join is pending approval. We'll email you once you're approved.");
        return;
      }
      toast.success("Welcome to Friday Conversations!");
      navigate("/portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your request");
    } finally {
      setLoading(false);
    }
  };

  const showBanner = !!(inviteInfo || ref);

  return (
    <div className="fc-hero-bg fc-paper-noise flex min-h-screen flex-col items-center justify-center px-4 py-10">
      <div className="mb-6"><Wordmark withTagline /></div>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="w-full max-w-md">
        <Card className="border-border bg-card shadow-[0_18px_50px_rgba(20,20,20,0.10)]">
          <CardContent className="p-7">
            {pendingMsg ? (
              <div className="flex flex-col items-center gap-4 py-4 text-center" data-testid="register-pending">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <CheckCircle2 className="h-7 w-7 text-primary" />
                </div>
                <h1 className="font-serif text-2xl font-semibold text-foreground">Request received</h1>
                <p className="text-sm text-muted-foreground">{pendingMsg}</p>
                <div className="mt-2 flex w-full flex-col gap-2">
                  <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]"><Link to="/">Back to home</Link></Button>
                  <Button asChild variant="ghost" className="w-full"><Link to="/login">Go to sign in</Link></Button>
                </div>
              </div>
            ) : (
            <>
            {showBanner && (
              <div className="mb-5 flex items-start gap-3 rounded-xl border border-[rgba(201,119,46,0.45)] bg-[rgba(201,119,46,0.16)] p-3.5" data-testid="register-invite-banner">
                <PartyPopper className="mt-0.5 h-5 w-5 text-[#8A4A18]" />
                <div className="text-sm text-[#8A4A18]">
                  {inviteInfo?.inviter_name
                    ? <>You’ve been invited by <strong>{inviteInfo.inviter_name}</strong>{inviteInfo.label ? <> · {inviteInfo.label}</> : null}.</>
                    : <>You were invited by a member.</>}{" "}
                  Create your account to join the conversation.
                </div>
              </div>
            )}

            <h1 className="font-serif text-2xl font-semibold text-foreground">Join the group</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {showBanner
                ? "You were invited — create your account and you're in right away."
                : "Request your membership — an organizer approves new members."}
            </p>

            <Button type="button" variant="secondary" className="mt-6 w-full" onClick={startGoogleLogin} data-testid="register-google-button">
              <GoogleIcon /> <span className="ml-2">Sign up with Google</span>
            </Button>
            <div className="my-5 flex items-center gap-3"><Separator className="flex-1" /><span className="text-xs text-muted-foreground">or</span><Separator className="flex-1" /></div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan Member" className="mt-1.5" data-testid="register-name-input" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5" data-testid="register-email-input" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="mt-1.5" data-testid="register-password-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="register-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="mr-2 h-4 w-4" /> Create account</>}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              Already a member?{" "}
              <Link to="/login" className="font-semibold text-primary hover:underline">Sign in</Link>
            </p>
            </>
            )}
          </CardContent>
        </Card>
        <div className="mt-4"><PrivacyPromise /></div>
      </motion.div>
    </div>
  );
}
